<script setup lang="ts">
import type { BreadcrumbProps } from '@nuxt/ui';
import type { FoldableBreadcrumbItem } from '#layers/ui/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { omit } from '#layers/base/public';
import { WorkspaceNavigationSelector } from '#layers/spaces/public';
import { useActiveSystemSpace, useVisibleSystemSpaces } from '#layers/ui-workspace/composables/use-system-space-navigation';
import { BreadcrumbFoldable, usePageBreadcrumbItems, usePaneStore } from '#layers/ui/public';
import SystemSpaceSwitcher from './SystemSpaceSwitcher.vue';
import WorkspaceSwitcher from './WorkspaceSwitcher.vue';

interface Props {
	items?: FoldableBreadcrumbItem[];
	/** Prepend the space-switcher crumb */
	showSpaceSwitcher?: boolean;
	/** Forwarded to `BreadcrumbFoldable`, merged over its internal default. */
	ui?: BreadcrumbProps['ui'];
}

const { items = [], showSpaceSwitcher = true, ui } = defineProps<Props>();

const SWITCHER_SLOTS = ['workspace', 'space'];

const paneStore = usePaneStore();
const pageItems = usePageBreadcrumbItems();
const activeSystemSpace = useActiveSystemSpace();
const visibleSystemSpaces = useVisibleSystemSpaces();
const { t } = useI18n();

const spaceItem = computed<FoldableBreadcrumbItem>(() => {
	// Outside system spaces, this crumb is the content-space selector
	if (!activeSystemSpace.value || visibleSystemSpaces.value.length > 1) return { slot: 'space', switcher: true };

	const { id, labelKey } = activeSystemSpace.value;
	const label = t(labelKey);
	const isVisible = visibleSystemSpaces.value.some((visible) => visible.id === id);

	return isVisible ? { label, to: { name: id } } : { label };
});

const breadcrumbItems = computed<FoldableBreadcrumbItem[]>(() => [
	...(paneStore.sidebarSplitPanelCollapsed ? [{ slot: 'workspace', switcher: true }] : []),
	...(showSpaceSwitcher ? [spaceItem.value] : []),
	...items,
	...pageItems.value,
]);
</script>

<template>
	<BreadcrumbFoldable :items="breadcrumbItems" :ui>
		<template #workspace="{ folded }">
			<WorkspaceSwitcher placement="breadcrumb" :folded />
		</template>

		<template v-if="showSpaceSwitcher" #space="{ folded }">
			<SystemSpaceSwitcher v-if="activeSystemSpace" :folded />
			<WorkspaceNavigationSelector v-else :folded />
		</template>

		<template v-for="(_, name) in omit($slots, SWITCHER_SLOTS)" #[name]="slotProps">
			<slot :name v-bind="slotProps" />
		</template>
	</BreadcrumbFoldable>
</template>
